import { useState, useMemo } from 'react'
import { ArrowLeft, Lock, AlertCircle, AlertTriangle } from 'lucide-react'
import { useWizardReviewController, DuplicatePair } from '@/hooks/useWizardReviewController'
import { ReviewSummaryHeader } from './ReviewSummaryHeader'
import { ReviewSettingsPanel } from './ReviewSettingsPanel'
import { DuplicateWarningBanner } from './DuplicateWarningBanner'
import { ReviewExpandedContent } from './ReviewExpandedContent'
import { DEFAULT_REVIEW_COLUMNS, getVisibleReviewColumns } from './reviewTableConfig'
import { DataTable } from '@/components/common/DataTable'
import { ColumnVisibilityControl } from '@/components/ui/ColumnVisibilityControl'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DriverMergeDialog } from '@/components/driver-master/DriverMergeDialog'
import { DriverMasterService } from '@/services/driverMasterService'
import { useAuth } from '@/contexts/AuthContext'
import { DriverMaster, DriverMergePreview, DriverMergeOptions } from '@/types/driverMaster'
import { toast } from 'sonner'

interface WizardReviewContentProps {
  periodId: string
  onBack: () => void
  onLock: () => Promise<void>
  isLocking: boolean
}

export function WizardReviewContent({
  periodId,
  onBack,
  onLock,
  isLocking
}: WizardReviewContentProps) {
  const { currentUser } = useAuth()
  const controller = useWizardReviewController(periodId)
  const { state } = controller

  // Column visibility for DataTable
  const {
    columns: columnConfigs,
    visibleColumns,
    updateColumnVisibility,
    resetToDefaults
  } = useColumnVisibility({
    storageKey: 'wizardReviewColumns',
    defaultColumns: DEFAULT_REVIEW_COLUMNS
  })

  // Get visible columns based on configuration
  const displayColumns = useMemo(() => {
    const visibleColumnKeys = visibleColumns.map(col => col.key)
    return getVisibleReviewColumns(visibleColumnKeys, state.excludedDriverIds)
  }, [visibleColumns, state.excludedDriverIds])

  // Sort snapshots: included first, then excluded
  const sortedSnapshots = useMemo(() => {
    return [...state.snapshots].sort((a, b) => {
      const aExcluded = state.excludedDriverIds.has(a.driverId)
      const bExcluded = state.excludedDriverIds.has(b.driverId)
      if (aExcluded !== bExcluded) return aExcluded ? 1 : -1
      return a.driverInfo.name.localeCompare(b.driverInfo.name, 'sv')
    })
  }, [state.snapshots, state.excludedDriverIds])

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const handleRowExpand = (rowId: string | number) => {
    const id = String(rowId)
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  // Lock confirmation dialog state
  const [showLockDialog, setShowLockDialog] = useState(false)
  const [hasReviewedConfirmation, setHasReviewedConfirmation] = useState(false)

  // Merge dialog state
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [mergePrimaryDriver, setMergePrimaryDriver] = useState<DriverMaster | null>(null)
  const [mergeSecondaryDriver, setMergeSecondaryDriver] = useState<DriverMaster | null>(null)
  const [mergePreview, setMergePreview] = useState<DriverMergePreview | null>(null)
  const [mergeLoading, setMergeLoading] = useState(false)
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false)

  // Separate included and excluded drivers
  const includedSnapshots = state.snapshots.filter(
    s => !state.excludedDriverIds.has(s.driverId)
  ).sort((a, b) => a.driverInfo.name.localeCompare(b.driverInfo.name, 'sv'))

  const excludedSnapshots = state.snapshots.filter(
    s => state.excludedDriverIds.has(s.driverId)
  ).sort((a, b) => a.driverInfo.name.localeCompare(b.driverInfo.name, 'sv'))

  const handleLockClick = async () => {
    // Save exclusions first
    try {
      await controller.saveExclusions()
    } catch (error) {
      toast.error('Kunde inte spara exkluderingar')
      return
    }

    // Show confirmation dialog
    setShowLockDialog(true)
    setHasReviewedConfirmation(false)
  }

  const handleConfirmLock = async () => {
    setShowLockDialog(false)
    await onLock()
  }

  const handleMergePair = async (pair: DuplicatePair) => {
    if (!currentUser) return

    try {
      setMergePreviewLoading(true)

      // Fetch both drivers
      const [driver1, driver2] = await Promise.all([
        DriverMasterService.getDriver(currentUser, pair.driver1Id),
        DriverMasterService.getDriver(currentUser, pair.driver2Id)
      ])

      if (!driver1 || !driver2) {
        toast.error('Kunde inte hämta förardata')
        return
      }

      setMergePrimaryDriver(driver1)
      setMergeSecondaryDriver(driver2)
      setMergeDialogOpen(true)

      // Generate merge preview
      const preview = await DriverMasterService.generateMergePreview(
        currentUser,
        driver1.id,
        driver2.id
      )
      setMergePreview(preview)
    } catch (error) {
      console.error('Failed to prepare merge:', error)
      toast.error('Kunde inte förbereda sammanslagning')
    } finally {
      setMergePreviewLoading(false)
    }
  }

  const handleConfirmMerge = async (options: DriverMergeOptions) => {
    if (!currentUser || !mergePrimaryDriver || !mergeSecondaryDriver) return

    try {
      setMergeLoading(true)
      await DriverMasterService.mergeDrivers(
        currentUser,
        mergePrimaryDriver.id,
        mergeSecondaryDriver.id,
        options
      )
      toast.success('Förare sammanslagna')
      setMergeDialogOpen(false)
      setMergePrimaryDriver(null)
      setMergeSecondaryDriver(null)
      setMergePreview(null)

      // Reload data to reflect the merge
      await controller.reloadData()
    } catch (error) {
      console.error('Failed to merge drivers:', error)
      toast.error('Kunde inte slå samman förare')
    } finally {
      setMergeLoading(false)
    }
  }

  const handleCloseMergeDialog = () => {
    setMergeDialogOpen(false)
    setMergePrimaryDriver(null)
    setMergeSecondaryDriver(null)
    setMergePreview(null)
  }

  // Loading state
  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  // Error state
  if (state.error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{state.error}</AlertDescription>
      </Alert>
    )
  }

  // Empty state
  if (state.snapshots.length === 0) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Granska</h1>
          <p className="text-muted-foreground">
            Kontrollera löneberäkningar innan låsning
          </p>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <p>Inga förare med intäkter för denna period.</p>
          <p className="text-sm mt-2">
            Ladda upp intäktsdata i föregående steg först.
          </p>
        </div>
        <div className="flex justify-start">
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Tillbaka
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Granska</h1>
        <p className="text-muted-foreground">
          Kontrollera löneberäkningar innan låsning
        </p>
      </div>

      {/* Summary header */}
      <ReviewSummaryHeader totals={state.periodTotals} />

      {/* Duplicate warning */}
      <DuplicateWarningBanner
        duplicates={state.potentialDuplicates}
        isExpanded={state.showDuplicateDetails}
        onToggleExpand={controller.toggleDuplicateDetails}
        onMergePair={handleMergePair}
      />

      {/* Settings panel */}
      <ReviewSettingsPanel
        settings={state.fleetSettings}
        isExpanded={state.showSettingsDetails}
        onToggleExpand={controller.toggleSettingsDetails}
      />

      {/* Column visibility control */}
      <div className="flex justify-end">
        <ColumnVisibilityControl
          columns={columnConfigs}
          onVisibilityChange={updateColumnVisibility}
          onResetToDefaults={resetToDefaults}
        />
      </div>

      {/* Driver table */}
      <DataTable
        data={sortedSnapshots}
        columns={displayColumns}
        expandableContent={(snapshot) => <ReviewExpandedContent snapshot={snapshot} />}
        expandedRows={expandedRows}
        onRowExpand={handleRowExpand}
        getRowId={(snapshot) => snapshot.driverId}
        getRowClassName={(snapshot) =>
          state.excludedDriverIds.has(snapshot.driverId) ? 'opacity-60' : ''
        }
        emptyMessage="Inga förare med intäkter för denna period."
      />

      {/* Action bar */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Tillbaka
        </Button>
        <Button
          onClick={handleLockClick}
          disabled={isLocking || includedSnapshots.length === 0}
          className="gap-2 bg-green-600 hover:bg-green-700"
        >
          {isLocking ? (
            <>
              <SpinnerIcon size="sm" className="text-white" />
              Låser period...
            </>
          ) : (
            <>
              <Lock className="h-4 w-4" />
              Lås period ({includedSnapshots.length} förare)
            </>
          )}
        </Button>
      </div>

      {/* Lock confirmation dialog */}
      <Dialog open={showLockDialog} onOpenChange={setShowLockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Lås period
            </DialogTitle>
            <DialogDescription>
              Du är på väg att låsa perioden med {includedSnapshots.length} förare.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {state.potentialDuplicates.length > 0 && (
              <Alert className="border-amber-500 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  Det finns {state.potentialDuplicates.length} potentiell{state.potentialDuplicates.length === 1 ? '' : 'a'} dubblett{state.potentialDuplicates.length === 1 ? '' : 'er'} som inte har åtgärdats.
                </AlertDescription>
              </Alert>
            )}

            {excludedSnapshots.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {excludedSnapshots.length} förare kommer att exkluderas från perioden.
              </p>
            )}

            <div className="flex items-start space-x-3">
              <Checkbox
                id="confirm-review"
                checked={hasReviewedConfirmation}
                onCheckedChange={(checked) => setHasReviewedConfirmation(checked === true)}
              />
              <label
                htmlFor="confirm-review"
                className="text-sm leading-relaxed cursor-pointer"
              >
                Jag har granskat löneberäkningarna och godkänner att perioden låses
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLockDialog(false)}>
              Avbryt
            </Button>
            <Button
              onClick={handleConfirmLock}
              disabled={!hasReviewedConfirmation}
              className="bg-green-600 hover:bg-green-700"
            >
              <Lock className="h-4 w-4 mr-2" />
              Lås period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge dialog */}
      {mergePrimaryDriver && mergeSecondaryDriver && (
        <DriverMergeDialog
          primaryDriver={mergePrimaryDriver}
          secondaryDriver={mergeSecondaryDriver}
          preview={mergePreview}
          open={mergeDialogOpen}
          onClose={handleCloseMergeDialog}
          onConfirm={handleConfirmMerge}
          loading={mergeLoading}
          previewLoading={mergePreviewLoading}
        />
      )}
    </div>
  )
}
